// @ai-generated 2026-07-10T00:00:00Z by matthias.gessinger@synqony.com using Claude Sonnet 5
import {
    type ApiAddress,
    type ApiContact,
    type ApiContactSnapshot,
    type ApiLocationSnapshot,
    type ApiRequisition,
} from '@Model';
import {
    type AsyncStateUpdate,
    Button,
    CollapsedSelectionCard,
    ConfirmationModal,
    EnumMessageLevel,
    FieldWithError,
    FlexRow,
    FloatingCard,
    iconSizeLg,
    iconSizeMd,
    type Index,
    InlineCreatablePicker,
    type InlineCreatablePickerItemPreview,
    isLinked,
    isOk,
    SearchableCardList,
    SelectionCard,
    useLinkFieldApi,
    useTranslate,
    useWizardData,
    useWizardErrors,
    useWizardUpdate,
    WizardStepTitle,
} from '@synqo/frontend-component-library';
import { IconMapPin, IconPlus } from '@tabler/icons-react';
import { useCallback, useState } from 'react';
import { siteSwitchMessage } from '../../containerOrder';
import { type RequisitionWizardState } from '../../RequisitionWizardState';
import { type NewAddressDraft, NewAddressModal } from './NewAddressModal';
import { hasSiteContact, siteContactDetail, siteContactName } from './siteContact';
import { type SiteContactDraft, SiteContactFields } from './SiteContactFields';
import { useSiteAddresses } from './useSiteAddresses';
import { type SiteChangeGuard, useSiteChangeGuard } from './useSiteChangeGuard';

export type SiteStepErrorKey = 'site';

interface SiteStepBodyProps {
    data: ApiRequisition;
    apiResourcePath: string;
    canEdit: boolean;
    /** Asks before the site changes. Absent where no positions depend on it. */
    siteChangeGuard?: SiteChangeGuard;

    requestUpdateState: AsyncStateUpdate<ApiRequisition>;
}

const contactToItem = (contact: ApiContact): InlineCreatablePickerItemPreview => ({
    label: contact.display_string || [contact.first_name, contact.last_name].filter(Boolean).join(' '),
    detail: contact.phone || contact.mobile || undefined,
});

export function SiteStepBody({
    data,
    apiResourcePath,
    requestUpdateState,
    canEdit,
    siteChangeGuard,
}: SiteStepBodyProps) {
    const i18n = useTranslate('');
    const errors = useWizardErrors<SiteStepErrorKey>();
    const feState = useWizardData<RequisitionWizardState>();
    const updateFeState = useWizardUpdate<RequisitionWizardState>();

    const linkSite = useLinkFieldApi<ApiRequisition, ApiAddress>(`${apiResourcePath}/${data.id}`, 'site');
    // "site_contact" is a virtual link field: the backend writes the linked contact into
    // site.contact, so there is no equally named property on ApiRequisition.
    const linkContact = useLinkFieldApi<ApiRequisition, ApiContact>(
        `${apiResourcePath}/${data.id}`,
        'site_contact' as Index<ApiRequisition>,
    );
    const {
        addresses,
        isLoading: addressesLoading,
        isFetching: addressesFetching,
        searchValue: addressQuery,
        setSearchValue: setAddressQuery,
        totalCount: addressCount,
        hasMore: hasMoreAddresses,
        onScroll: onAddressListScroll,
        recentlyUsedIds,
    } = useSiteAddresses(linkSite.loadLinkableObjects);

    const [addressModalOpen, setAddressModalOpen] = useState(false);
    const { guardSiteChange, confirmSiteChange, cancelSiteChange, isAsking } = useSiteChangeGuard(siteChangeGuard);

    const siteLinked = isLinked(data.site.address);
    const hasSelection = siteLinked || feState.siteUseSnapshot;
    const showPicker = feState.sitePicking || !hasSelection;

    const selectAddress = useCallback(
        (addressId: string) => {
            void requestUpdateState(async () => {
                const result = await linkSite.link(addressId);
                if (!isOk(result)) {
                    return { data: undefined, messages: result.messages };
                }
                updateFeState({ sitePicking: false, siteUseSnapshot: false });
                setAddressQuery('');
                return { data: result.data, messages: result.messages };
            });
        },
        [requestUpdateState, linkSite, updateFeState, setAddressQuery],
    );

    const deselectAddress = useCallback(() => {
        void requestUpdateState(async () => {
            const result = await linkSite.unlink();
            if (!isOk(result)) {
                return { data: undefined, messages: result.messages };
            }
            updateFeState({ sitePicking: true });
            setAddressQuery('');
            return { data: result.data, messages: result.messages };
        });
    }, [requestUpdateState, linkSite, updateFeState, setAddressQuery]);

    const saveNewAddress = useCallback(
        (draft: NewAddressDraft) => {
            void requestUpdateState(async (latest, saveData) => {
                let current = latest;
                // A linked site must be detached before the snapshot fields become authoritative.
                if (isLinked(current.site.address)) {
                    const unlinkResult = await linkSite.unlink();
                    if (!isOk(unlinkResult)) {
                        return { data: undefined, messages: unlinkResult.messages };
                    }
                    current = unlinkResult.data;
                }
                const hasContact = draft.contactFirstName.trim() || draft.contactLastName.trim();
                const contact: ApiContactSnapshot | undefined = hasContact
                    ? {
                          ...current.site.contact,
                          foreign_key: undefined,
                          first_name: draft.contactFirstName.trim(),
                          last_name: draft.contactLastName.trim(),
                          phone: draft.contactPhone.trim(),
                          mobile: '',
                          email: '',
                      }
                    : current.site.contact;
                const site: ApiLocationSnapshot = {
                    address: {
                        ...current.site.address,
                        label: draft.label,
                        street: draft.street,
                        house_number: draft.houseNumber,
                        zip_code: draft.zipCode,
                        city: draft.city,
                    },
                    contact,
                };
                const remarks = draft.accessHint
                    ? [current.remarks, `${i18n('Zufahrtshinweis')}: ${draft.accessHint}`].filter(Boolean).join('\n')
                    : current.remarks;
                const result = await saveData({ ...current, site, remarks });
                if (isOk(result)) {
                    updateFeState({ sitePicking: false, siteUseSnapshot: true });
                    setAddressModalOpen(false);
                    setAddressQuery('');
                }
                return result;
            });
        },
        [requestUpdateState, linkSite, updateFeState, setAddressQuery, i18n],
    );

    /**
     * Takes the address the customer typed in, once they have agreed to leave the current site.
     *
     * Asked at the save rather than at the button, because that is the moment the site actually
     * changes — and with the form closed rather than underneath the question, so that saying no
     * leaves the draft standing: reopening the modal brings it back as it was.
     */
    // @ai-generated 2026-09-16T00:00:00+00:00 by matthias.gessinger@synqony.com using Claude Opus 5 (1M context)
    const requestNewAddress = useCallback(
        (draft: NewAddressDraft) => {
            setAddressModalOpen(false);
            guardSiteChange(
                () => saveNewAddress(draft),
                () => setAddressModalOpen(true),
            );
        },
        [guardSiteChange, saveNewAddress],
    );

    const selectContact = useCallback(
        (id: string) => {
            void requestUpdateState(async () => {
                const result = await linkContact.link(id);
                if (!isOk(result)) {
                    return { data: undefined, messages: result.messages };
                }
                return { data: result.data, messages: result.messages };
            });
        },
        [requestUpdateState, linkContact],
    );

    const clearContact = useCallback(() => {
        void requestUpdateState(async () => {
            const result = await linkContact.unlink();
            if (!isOk(result)) {
                return { data: undefined, messages: result.messages };
            }
            return { data: result.data, messages: result.messages };
        });
    }, [requestUpdateState, linkContact]);

    // Inline-captured contacts become an unlinked snapshot inside the site snapshot — mirroring
    // the "Neue Anschrift anlegen" flow for the site.
    const createContact = useCallback(
        (draft: SiteContactDraft) => {
            void requestUpdateState(async (latest, saveData) => {
                const contact: ApiContactSnapshot = {
                    ...latest.site.contact,
                    foreign_key: undefined,
                    first_name: draft.firstName.trim(),
                    last_name: draft.lastName.trim(),
                    phone: draft.phone.trim(),
                    mobile: '',
                    email: '',
                };
                return await saveData({ ...latest, site: { ...latest.site, contact } });
            });
        },
        [requestUpdateState],
    );

    // "50 von 3000" — a plain "50 Treffer" would look like the whole result set.
    const listLabel = hasMoreAddresses
        ? `${addresses.length} ${i18n('von')} ${addressCount}`
        : addressQuery.trim()
          ? `${addresses.length} ${i18n('Treffer')}`
          : i18n('Alle Leistungsorte');

    return (
        <>
            <WizardStepTitle
                title={i18n('Wo soll die Leistung erbracht werden?')}
                subtitle={i18n(
                    'Wähle die Anfallstelle bzw. den Leistungsort. Für eine neue Baustelle kannst du direkt eine Anschrift anlegen.',
                )}
            />

            <FieldWithError error={!hasSelection ? errors.site : undefined}>
                {!showPicker && (
                    <CollapsedSelectionCard
                        title={data.site.address.label || data.site.address.display_string}
                        subtitle={data.site.address.display_string}
                        icon={<IconMapPin size={iconSizeLg} />}
                        actionLabel={i18n('Ändern')}
                        onAction={() => guardSiteChange(deselectAddress)}
                    >
                        <InlineCreatablePicker<ApiContact>
                            name="site_contact"
                            loadLinkableObjects={linkContact.loadLinkableObjects}
                            queryKey={[apiResourcePath, data.id, 'site_contact']}
                            toItem={contactToItem}
                            value={isLinked(data.site.contact) ? data.site.contact.foreign_key : undefined}
                            selected={
                                hasSiteContact(data.site.contact)
                                    ? {
                                          label: siteContactName(data.site.contact),
                                          detail: siteContactDetail(data.site.contact),
                                      }
                                    : undefined
                            }
                            onSelect={selectContact}
                            onClear={clearContact}
                            renderCreateForm={({ close }) => (
                                <NewContactForm onCreate={createContact} onClose={close} i18n={i18n} />
                            )}
                            labels={{
                                addAction: i18n('Ansprechpartner hinzufügen'),
                                remove: i18n('Entfernen'),
                                selectedPrefix: i18n('Ansprechpartner'),
                                createAction: i18n('Neuen Ansprechpartner anlegen'),
                            }}
                            locked={!canEdit}
                        />
                    </CollapsedSelectionCard>
                )}

                {showPicker && (
                    <FloatingCard title="" collapsible={false} separateHeader={false}>
                        <SearchableCardList
                            name="site_picker"
                            label={i18n('Leistungsorte')}
                            searchValue={addressQuery}
                            onSearchChange={setAddressQuery}
                            searchPlaceholder={i18n('Leistungsort suchen (Name, Straße, Ort) …')}
                            listLabel={listLabel}
                            onScroll={onAddressListScroll}
                            isLoadingMore={hasMoreAddresses && addressesFetching}
                            isEmpty={!addressesLoading && addresses.length === 0}
                            emptyText={i18n('Kein Leistungsort gefunden. Lege eine neue Anschrift an.')}
                            maxHeight="18.5rem"
                            footer={
                                <Button
                                    appearance="dashed"
                                    fullWidth
                                    onClick={() => setAddressModalOpen(true)}
                                    locked={!canEdit}
                                    label={i18n('Neue Anschrift anlegen')}
                                >
                                    <IconPlus size={iconSizeMd} />
                                    {i18n('Neue Anschrift anlegen')}
                                </Button>
                            }
                        >
                            {addresses.map((address) => (
                                <SelectionCard
                                    key={address.id}
                                    title={address.label}
                                    subtitle={address.display_string}
                                    badge={recentlyUsedIds.has(address.id) ? i18n('zuletzt verwendet') : undefined}
                                    icon={<IconMapPin size={iconSizeMd} />}
                                    selected={siteLinked && data.site.address.foreign_key === address.id}
                                    onClick={() =>
                                        data.site.address.foreign_key === address.id
                                            ? selectAddress(address.id)
                                            : guardSiteChange(() => selectAddress(address.id))
                                    }
                                    locked={!canEdit}
                                />
                            ))}
                        </SearchableCardList>
                    </FloatingCard>
                )}
            </FieldWithError>

            <NewAddressModal
                isOpen={addressModalOpen}
                onClose={() => setAddressModalOpen(false)}
                onSave={requestNewAddress}
            />

            <ConfirmationModal
                isOpen={isAsking}
                title={i18n('Leistungsort wechseln?')}
                message={i18n(siteSwitchMessage(siteChangeGuard?.positionCount ?? 0))}
                messageLevel={EnumMessageLevel.Information}
                modalButtonContent={i18n('Ort wechseln')}
                onConfirm={confirmSiteChange}
                onCancel={cancelSiteChange}
            />
        </>
    );
}

interface NewContactFormProps {
    onCreate: (draft: SiteContactDraft) => void;
    onClose: () => void;
    i18n: (key: string) => string;
}

/** Inline "neuen Ansprechpartner anlegen" form shown by the picker's `renderCreateForm`. */
function NewContactForm({ onCreate, onClose, i18n }: NewContactFormProps) {
    const [draft, setDraft] = useState<SiteContactDraft>({ firstName: '', lastName: '', phone: '' });

    const save = () => {
        onCreate(draft);
        onClose();
    };

    return (
        <>
            <SiteContactFields
                idPrefix="new_site_contact"
                value={draft}
                onChange={(patch) => setDraft((previous) => ({ ...previous, ...patch }))}
                autoFocusOnRender
            />
            <FlexRow justify="flex-end" gap="sm">
                <Button appearance="outlined" variant="normal" size="sm" onClick={onClose} label={i18n('Abbrechen')}>
                    {i18n('Abbrechen')}
                </Button>
                <Button
                    appearance="filled"
                    variant="primary"
                    size="sm"
                    onClick={save}
                    locked={!draft.firstName.trim() && !draft.lastName.trim()}
                    label={i18n('Ansprechpartner speichern')}
                >
                    {i18n('Ansprechpartner speichern')}
                </Button>
            </FlexRow>
        </>
    );
}
